import { useMemo, useState } from 'react';
import { FieldData } from '@/components/Form/types';
import useLabels from '@/hooks/useApi/useLabels';
import { Select, TextField } from 'vopay';
import { CircleButton } from 'UI';
import { useForm } from '@/components/Form/utils';

export type FormValues = {
  clientType: 'individual' | 'business' | '';
  accountStatus: 'active' | 'inactive' | '';
  keyword: string;
  label: string;
};

const initialValues: FormValues = {
  accountStatus: '',
  keyword: '',
  clientType: '',
  label: '',
};

export function useFilterFormConfig({ onSubmit }: { onSubmit: (values: FormValues) => void }) {
  const config = useConfig();

  const { renderFormElements, ctx } = useForm<FormValues>({
    initialValues,
    config,
    onSubmit,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}

function useConfig(): FieldData<FormValues>[] {
  const { labels } = useLabels();
  const [searchTerm, setSearchTerm] = useState('');
  const filteredLabels = useMemo(
    () =>
      labels
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 100)
        .map((label) => ({ label, value: label })),
    [labels, searchTerm],
  );

  return [
    {
      fieldName: 'clientType',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['clientType']>({})}
            sort={false}
            placeholder="Type"
            showClear
            options={[
              { label: 'Individual', value: 'individual' },
              { label: 'Business', value: 'business' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'accountStatus',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['accountStatus']>({})}
            sort={false}
            placeholder="Status"
            showClear
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'label',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['label']>({})}
            sort={false}
            showClear
            placeholder="Label"
            filterable
            options={filteredLabels}
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => {
                setSearchTerm(e.filter);
              }, 300);
            }}
          />
        );
      },
    },
    {
      fieldName: 'keyword',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        return (
          <TextField
            variant="outlineShadow"
            {...getFormikData<FormValues['keyword']>({})}
            placeholder="Keyword"
            elementRight={
              ctx.values.keyword ? (
                <CircleButton
                  type="button"
                  icon="close"
                  circleFill={false}
                  onClick={() => ctx.setFieldValue('keyword', '')}
                  style={{
                    marginRight: '8px',
                  }}
                />
              ) : (
                false
              )
            }
          />
        );
      },
    },
  ];
}
